import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GitBranch, Download, RefreshCw } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useGitSyncStore } from '../../store/gitSyncStore';
import { isGitTracked, refreshGitStatus } from '../../services/git/gitSyncStatus';
import { setShowWorkspaceModal, setWorkspaceModalMode } from '../../store/uiModalStore';
import { isTauri } from '../../utils/tauri';
import GitAccounts from './GitAccounts';
import GitScopeHost, { useScopeGitHost } from './GitScopeHost';
import GitTargetPills from './GitTargetPills';
import GitDetail from './GitDetail';
import { gitTargets, pickTarget } from './gitTargets';
import './GitView.css';

/**
 * The Git page (git sync Stage 1, card 7 — owner, 28.09.2026: "one home for
 * everything git; not a git client"). Card 8 made it the only place for git:
 * account sign-in, connect, push / pull and clone all live here; the
 * workspace menu and Settings only point to it. Stage 2 (S2.3) added
 * projects, boards & documents: one repository for your own items and one
 * per team.
 *
 * Redesign (owner's mock, 05.10.2026): the accounts are one chip in the
 * header; every workspace and scope is a pill (grouped, with its state); the
 * selected one fills one detail card — head with its actions, then Changes |
 * History, or the way to connect it.
 *
 * Actions reuse the existing handlers (useWorkspaces → App), so connect,
 * secrets and conflicts keep their behaviour; a scope's are its host's
 * (GitScopeHost).
 */
export default function GitView(props) {
  // Projects, boards & documents exist in the desktop app only.
  if (!isTauri()) return <GitPage {...props} />;
  return (
    <GitScopeHost>
      <GitPage {...props} />
    </GitScopeHost>
  );
}

function GitPage({ onGitSync, onGitPull, onGitAddRemote, onGitStopTracking, onLoadChanges, onPutBack, gitSyncingWorkspaceId }) {
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const byWorkspace = useGitSyncStore((s) => s.byWorkspace);
  const focusId = useGitSyncStore((s) => s.focusId);
  const host = useScopeGitHost();
  const targets = useMemo(() => gitTargets(workspaces, host?.scopes || [], byWorkspace), [workspaces, host?.scopes, byWorkspace]);

  // Fresh state for every tracked workspace when the page opens, and again on
  // the title bar's "Check sync status" (ExtensionTitleBarActions). Scopes: GitScopeHost.
  const linked = (workspaces || []).filter((ws) => ws.storage !== 'git' && isGitTracked(ws));
  const linkedKey = linked.map((w) => w.id).join(',');
  useEffect(() => {
    const check = () => linked.forEach((ws) => refreshGitStatus(ws));
    check();
    window.addEventListener('git-refresh-status', check);
    return () => window.removeEventListener('git-refresh-status', check);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedKey]);

  // The one shown: the one picked, else the first connected, else the first.
  const [selectedId, setSelectedId] = useState(null);
  const selected = pickTarget(targets, selectedId);

  // Opened from a workspace's "…" menu: show that workspace, its card scrolled
  // to and briefly highlighted (as an editor reveals the file you asked for).
  // (Clearing focusId re-runs this effect, so the reveal's timers live in a ref
  // and are only cancelled on unmount — not by that re-run.)
  const revealTimers = useRef([]);
  useEffect(() => () => revealTimers.current.forEach((cancel) => cancel()), []);
  useEffect(() => {
    if (focusId == null) return;
    setSelectedId(focusId);
    useGitSyncStore.getState().setFocus(null);
    const raf = requestAnimationFrame(() => {
      const card = document.querySelector('[data-testid="git-detail"]');
      if (!card) return;
      card.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      card.classList.add('git-row--reveal');
      const timer = setTimeout(() => card.classList.remove('git-row--reveal'), 1800);
      revealTimers.current.push(() => clearTimeout(timer));
    });
    revealTimers.current.push(() => cancelAnimationFrame(raf));
  }, [focusId]);

  const openClone = () => {
    setWorkspaceModalMode('clone');
    setShowWorkspaceModal(true);
  };

  return (
    <OsScroll className="git-page-scroll">
      <div className="git-page" data-testid="git-page">
        <header className="git-head">
          <div className="git-head-title">
            <div className="git-head-name">
              <GitBranch size={20} />
              <h1>Git</h1>
            </div>
            <span className="git-head-sub">Keep your workspaces, projects, boards and documents in your own repositories</span>
          </div>
          <div className="git-head-actions">
            {/* "Check sync status" left the title bar (owner, 20.10.2026); same event. */}
            <button
              type="button"
              className="git-btn git-btn--ghost git-btn--large"
              onClick={() => window.dispatchEvent(new CustomEvent('git-refresh-status'))}
            >
              <RefreshCw size={14} /> Check status
            </button>
            <GitAccounts />
            {isTauri() && (
              <button type="button" className="git-btn git-btn--ghost git-btn--large" onClick={openClone}>
                <Download size={14} /> Clone a repository
              </button>
            )}
          </div>
        </header>

        {targets.length === 0 ? (
          <p className="git-empty">No workspace yet. Create one, then connect it here.</p>
        ) : (
          <>
            <GitTargetPills targets={targets} selectedId={selected?.id} onSelect={setSelectedId} />
            <GitDetail
              key={selected?.id}
              target={selected}
              byWorkspace={byWorkspace}
              gitSyncingWorkspaceId={gitSyncingWorkspaceId}
              onGitSync={onGitSync}
              onGitPull={onGitPull}
              onGitAddRemote={onGitAddRemote}
              onGitStopTracking={onGitStopTracking}
              onLoadChanges={onLoadChanges}
              onPutBack={onPutBack}
            />
          </>
        )}
      </div>
    </OsScroll>
  );
}
